import type { Lead } from '@/lib/types'
import OriginBadge from '@/components/shared/OriginBadge'
import { IconClock, IconUser, IconBuilding, IconAlertCircle, IconCheck, IconXCircle } from '@/components/icons/Icons'

interface LeadCardProps {
  lead: Lead
  daysInStage: number
  onClick: () => void
  borderColor: string
  isSelected: boolean
  onToggleSelect: (id: number) => void
}

export default function LeadCard({ lead, daysInStage, onClick, borderColor, isSelected, onToggleSelect }: LeadCardProps) {
  const principal = lead.contactos?.find(c => c.es_principal) || lead.contactos?.[0]
  const contactName = principal ? `${principal.nombre}${principal.apellido ? ` ${principal.apellido}` : ''}` : null
  const extraCount = (lead.contactos?.length || 0) - 1
  // Mismo orden que la ficha: titulo > empresa > codigo. La cursiva marca que
  // el nombre es provisional (todavia nadie nombro la oportunidad).
  const nombreVisible = lead.titulo || lead.empresa || lead.codigo
  const hasTitle = !!lead.titulo
  const hasContact = !!contactName

  function handleDragStart(e: React.DragEvent) {
    e.dataTransfer.setData('text/plain', String(lead.id))
    e.dataTransfer.effectAllowed = 'move'
    ;(e.currentTarget as HTMLElement).classList.add('dragging')
  }

  function handleDragEnd(e: React.DragEvent) {
    ;(e.currentTarget as HTMLElement).classList.remove('dragging')
  }

  return (
    <div
      className={`crm-lead-card${isSelected ? ' crm-lead-card--selected' : ''}`}
      style={{ borderLeftColor: borderColor }}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={onClick}
      id={`lead-card-${lead.id}`}
    >
      <div className="crm-lead-card__header-row">
        <input
          type="checkbox"
          className="crm-lead-card__checkbox"
          checked={isSelected}
          onChange={e => { e.stopPropagation(); onToggleSelect(lead.id) }}
          onClick={e => e.stopPropagation()}
        />
        <div className="crm-lead-card__content">
          <div className={`crm-lead-card__name${!hasTitle ? ' crm-lead-card__name--generated' : ''}`}>
            {nombreVisible}
          </div>
          <div className="crm-lead-card__secondary">
            {hasContact ? (
              <span className="crm-lead-card__contact">
                <IconUser size={11} /> {contactName}
                {extraCount > 0 && <span className="crm-lead-card__extra-contacts">+{extraCount}</span>}
              </span>
            ) : (
              <span className="crm-lead-card__no-contact">
                <IconAlertCircle size={11} /> Sin contacto
              </span>
            )}
            {lead.empresa && lead.empresa !== nombreVisible && (
              <span className="crm-lead-card__empresa">
                <IconBuilding size={11} /> {lead.empresa}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="crm-lead-card__footer">
        <div className="crm-lead-card__badges">
          {lead.origen && <OriginBadge origen={lead.origen} />}
          {/* Emoji fuera: se ve distinto en cada sistema y no hereda el color
              del texto. El ícono sí (regla del design system). */}
          {lead.es_agencia && (
            <span className="crm-lead-badge crm-lead-badge--agency"><IconBuilding size={11} /> Agencia</span>
          )}
          {/* Sin dueño: entró sola (formulario de la web, booking, chatbot) y
              nadie la tomó todavía. Es el único estado del tablero que pide
              una acción de alguien en particular — por eso se ve desde la
              tarjeta, sin tener que abrirla una por una. */}
          {lead.responsable_id == null && (
            <span className="crm-lead-badge crm-lead-badge--huerfano"><IconUser size={11} /> Sin asignar</span>
          )}
          {/* Ganado o perdido: la columna Cerrado los mezcla, y sin esto las
              dos tarjetas se leen igual. */}
          {lead.resultado && (
            <span className={`ld-resultado ld-resultado--${lead.resultado} ld-resultado--mini`}>
              {lead.resultado === 'ganado' ? <IconCheck size={11} /> : <IconXCircle size={11} />}
              {lead.resultado === 'ganado' ? 'Ganado' : 'Perdido'}
            </span>
          )}
        </div>
        <span className="crm-lead-card__days"><IconClock size={12} /> {daysInStage}d</span>
      </div>
    </div>
  )
}
